import { FormattedMessage } from 'react-intl';
import { type ReactNode } from 'react';
import cx from 'clsx';
import { FormattedMoney } from '@repo/design-system-kit';
import { oppositeSign } from '../../../utils';
import { formatAsPercent } from '../../../utils/format-as-percent.ts';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';
import type { Discount } from '../../types';
import styles from './item-discount-row.module.css';

interface ItemDiscountRowProps {
  discount?: Discount;
  totalDiscount?: string;
  hideBorder?: boolean;
  index: number;
}

export function ItemDiscountRow({
  discount,
  hideBorder,
  index,
  totalDiscount,
}: ItemDiscountRowProps): ReactNode {
  const { currency, displayLanguage, isPartialInvoice } = usePdfPreviewContext();

  if (discount) {
    // totalDiscount is more reliable especially for partial invoices as it's dynamic
    // when migrating model to React, we may need to check what makes sense to use
    const discountAmount = isPartialInvoice ? totalDiscount : discount.amount || totalDiscount;

    return (
      <tr
        className={cx(styles.discount, hideBorder && styles['no-bottom'])}
        data-testid={`pdf-preview-items-row-discount-${index}`}
      >
        <td className="body-1" colSpan={isPartialInvoice ? 6 : 4}>
          {discount.type === 'percentage' ? (
            <p className={styles.description} data-testid="pdf-preview-item-discount-percentage">
              <FormattedMessage
                id="pdf-preview.percentage-discount"
                values={{
                  language: displayLanguage,
                  discountPercentage: formatAsPercent(discount.value ?? 0),
                }}
              />
            </p>
          ) : (
            <p className={styles.description} data-testid="pdf-preview-item-discount-amount">
              <FormattedMessage
                id="pdf-preview.amount-discount"
                values={{ language: displayLanguage }}
              />
            </p>
          )}
        </td>
        <td className="body-1" data-testid="pdf-preview-item-discount-total-amount">
          {discountAmount ? (
            <p className={styles.description}>
              <FormattedMoney
                amount={oppositeSign(Number(discountAmount) || 0)}
                currency={currency}
                token="money-symbol"
              />
            </p>
          ) : null}
        </td>
      </tr>
    );
  }
}
